/**
 * Help-article registry.
 *
 * Articles are bundled at build time via Vite's `import.meta.glob` with
 * `query: '?raw'`, so every Markdown file under `./articles/*.md` ships in the
 * client bundle as a string. This is the Phase 7A lookup mechanism — Phase 7B
 * will replace this resolver with a DB-first / bundled-fallback lookup, but
 * `getArticle(slug)` keeps the same signature so the call sites do not change.
 *
 * Slug derivation: filename without `.md`, with any leading underscore
 * stripped. So `articles/overview.md` → slug `overview`, and
 * `articles/_default.md` → slug `default`.
 *
 * Fail-loud: if any bundled Markdown file is missing an `# H1` line we throw
 * at module load. A help article without a title is a content bug, not a
 * runtime branch — surfacing it at boot beats rendering an empty header in
 * production.
 */

export type HelpArticle = {
	slug: string;
	title: string;
	body: string;
	headings: string[];
};

const RAW_ARTICLES = import.meta.glob<string>("./articles/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

const ARTICLES: Map<string, HelpArticle> = buildArticles(RAW_ARTICLES);

function buildArticles(raw: Record<string, string>): Map<string, HelpArticle> {
	const out = new Map<string, HelpArticle>();
	for (const [path, body] of Object.entries(raw)) {
		const slug = slugFromPath(path);
		const title = extractTitle(body, path);
		const headings = extractHeadings(body);
		out.set(slug, { slug, title, body, headings });
	}
	return out;
}

function slugFromPath(path: string): string {
	// path looks like "./articles/overview.md" or "./articles/_default.md"
	const filename = path.split("/").pop() ?? path;
	const withoutExt = filename.replace(/\.md$/, "");
	return withoutExt.replace(/^_+/, "");
}

function extractTitle(body: string, path: string): string {
	const lines = body.split("\n");
	for (const line of lines) {
		const match = /^#\s+(.+?)\s*$/.exec(line);
		if (match) return match[1] as string;
	}
	throw new Error(
		`Help article at ${path} is missing a top-level "# Title" line. Every article must start with an H1.`,
	);
}

function extractHeadings(body: string): string[] {
	const headings: string[] = [];
	for (const line of body.split("\n")) {
		const match = /^##\s+(.+?)\s*$/.exec(line);
		if (match) headings.push(match[1] as string);
	}
	return headings;
}

/**
 * Look up an article by slug. Returns `null` when no article is registered —
 * the caller renders the dialog's empty state ("Browse all topics") in that
 * case.
 */
export function getArticle(slug: string): HelpArticle | null {
	return ARTICLES.get(slug) ?? null;
}

/**
 * All bundled articles, sorted alphabetically by title for stable display in
 * the empty-state "Browse all topics" list.
 */
export function listArticles(): HelpArticle[] {
	return Array.from(ARTICLES.values()).sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Every slug currently registered. Useful for tests that assert every entry
 * in `HELP_SLUGS` has a corresponding bundled article (D4 will add that
 * cross-check).
 */
export function getAllSlugs(): readonly string[] {
	return Array.from(ARTICLES.keys());
}
